/**
 * AIO 対策ハブ。5 工程を 1 枚で見て、詰まっている工程から着手する。
 *
 * この面が守っていること:
 * - **見出しに置く数字は 1 つだけ**（回答可能率）。工程ごとの数字は合成しない
 * - **数えていないものを 0 と描かない。** `metric` / `have` が null の欄には
 *   「数えていません」と理由を出す（「—」だけを置かない）
 * - 工程 4（公開）は、この画面群では**配信をしない**ことを明示する。
 *   押せない導線を出さない
 */

import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "urql";
import {
  AioHubDocument,
  Card,
  Disclosure,
  ErrorState,
  LoadingState,
  Meter,
  PageHeader,
  Stat,
  Tag,
  formatJstDateTime,
  type PipelineState,
  type PopulationId,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  ANSWER_RATE_IS_A_SEPARATE_MEASURE_NOTE,
  NOT_MEASURED,
  PUBLISH_IS_NOT_IN_THIS_RELEASE,
} from "../api-capabilities";
import { PopulationSelect } from "./PopulationSelect";

const STATE_TONE: Record<PipelineState, "ok" | "warn" | "neutral"> = {
  OK: "ok",
  STUCK: "warn",
  UNKNOWN: "neutral",
};

function percent(rate: number | null | undefined): string {
  return rate == null ? "—" : `${Math.round(rate * 100)} %`;
}

export function AioPage() {
  // 1 画面 1 母数。ここを切り替えると、下の数字はすべてその母数のものになる
  const [population, setPopulation] = useState<PopulationId>("DELIVERED");
  const [result, refetch] = useQuery({
    query: AioHubDocument,
    variables: { population },
  });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const coverage = result.data.contextCoverage;
  const holdings = result.data.sourceHoldings;
  const stuck = coverage.stages.filter((stage) => stage.state === "STUCK");

  return (
    <div className="vd-page">
      <PageHeader
        title="AIO 対策"
        description="5 工程を 1 枚で見て、詰まっている工程から着手します。"
        titleExtra={<HelpIcon slug="aio/hub" />}
        extra={
          <span className="vd-row">
            <Stat
              label={`回答可能率（${coverage.basis.label}）`}
              value={percent(coverage.answerRate)}
              // 工程 1 の「数えていません」と並ぶと食い違って見えるので、
              // 定義が別物であることを数字と同じ塊に入れる
              note={ANSWER_RATE_IS_A_SEPARATE_MEASURE_NOTE}
            />
          </span>
        }
      />

      <div className="vd-stack">
        <Card
          title="どの商品を母数にするか"
          note="1 つの画面では 1 つの母数だけを使います。選べるのは、いま API が数えられる母集団だけです。"
        >
          <PopulationSelect value={population} onChange={setPopulation} />
        </Card>

        {stuck.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="status">
            <span className="vd-state-title">
              工程 {stuck.map((stage) => stage.no).join(" と ")}{" "}
              で詰まっています
            </span>
            {stuck.map((stage) => (
              <span key={stage.no}>
                ({stage.no}) {stage.title}: {stage.metricNote}
              </span>
            ))}
          </div>
        )}

        <Card
          title="AIO 対策の 5 工程"
          extra={<HelpIcon slug="aio/hub" anchor="AIO-対策の-5-工程" />}
          note={`工程ごとの数字は 1 つのスコアに合成していません。数えていない工程は「${NOT_MEASURED}」と出します。${coverage.basis.note}`}
        >
          <div className="vd-stack">
            {coverage.stages.map((stage) => (
              <div
                key={stage.no}
                style={{
                  borderTop: "1px solid var(--vd-border)",
                  paddingTop: 12,
                }}
              >
                <div
                  className="vd-row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="vd-card-title">
                    ({stage.no}) {stage.title}
                    <Tag tone={STATE_TONE[stage.state]}>{stage.stateLabel}</Tag>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {/* 数えていない欄に「—」だけを置かない。理由を必ず添える */}
                    <div className="vd-stat-value">
                      {stage.metric ?? NOT_MEASURED}
                    </div>
                    <div className="vd-stat-note">{stage.metricNote}</div>
                  </div>
                </div>
                <p
                  style={{
                    margin: "4px 0 8px",
                    color: "var(--vd-text-secondary)",
                  }}
                >
                  {stage.summary}
                </p>
                <div className="vd-row">
                  {stage.links.map((link) => (
                    <Link key={link.route} to={link.route}>
                      {link.label}
                    </Link>
                  ))}
                </div>
                {stage.dataGap === null ? null : (
                  <Disclosure
                    summary={`「${stage.dataGap.what}」を数えていない理由`}
                  >
                    <p style={{ margin: 0 }}>{stage.dataGap.why}</p>
                    <p style={{ margin: "4px 0 0" }}>
                      必要なもの: {stage.dataGap.needs}
                      {stage.dataGap.external === null
                        ? ""
                        : `（接続先: ${stage.dataGap.external}）`}
                    </p>
                  </Disclosure>
                )}
              </div>
            ))}
          </div>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {coverage.computedAt === null
              ? "回答可能率をいつ計算したかは分かりません。"
              : `回答可能率は ${formatJstDateTime(coverage.computedAt)} 時点のスナップショットです。`}
          </p>
        </Card>

        <Card
          title="出どころ別に、いま持っているもの"
          extra={
            <HelpIcon slug="aio/hub" anchor="出どころ別にいま持っているもの" />
          }
          note="持っている量だけを書きます。「あと何件足りない」は、持っていない側の総数を数えていないので書きません。"
        >
          <div className="vd-table-scroll">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>出どころ</th>
                  <th>中身</th>
                  <th>いま持っている量</th>
                  <th>数えた内訳</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <tr key={`${holding.source}-${holding.label}`}>
                    <td>{holding.source}</td>
                    <td>{holding.label}</td>
                    <td>
                      {/* null は「数えていない」。0 件と混ぜない */}
                      {holding.have == null
                        ? NOT_MEASURED
                        : holding.have.toLocaleString("ja-JP")}
                    </td>
                    <td>{holding.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card
          title="公開の内訳（工程 4）"
          extra={<HelpIcon slug="aio/hub" anchor="公開の内訳工程-4" />}
          note={PUBLISH_IS_NOT_IN_THIS_RELEASE}
        >
          <p style={{ margin: 0 }}>
            本文と JSON-LD は商品ごとに組み立てて中身を見られます。
            <Link to="/products">商品一覧</Link>
            から商品を開いてください。
          </p>
        </Card>

        <Card
          title="答えられている割合"
          extra={<HelpIcon slug="aio/hub" />}
          note={`この画面の数字はすべて「${coverage.basis.label}」を母数にしています。`}
        >
          <div className="vd-row" style={{ gap: 24, alignItems: "flex-end" }}>
            <Stat
              hero
              label={coverage.basis.label}
              value={percent(coverage.answerRate)}
              note={
                coverage.basis.denominator === null
                  ? undefined
                  : `母数 ${coverage.basis.denominator.toLocaleString("ja-JP")} 件`
              }
            />
            <div style={{ flex: "1 1 240px", minWidth: 200 }}>
              <Meter ratio={coverage.answerRate ?? null} />
            </div>
          </div>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {coverage.basis.note}
          </p>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {ANSWER_RATE_IS_A_SEPARATE_MEASURE_NOTE}
          </p>
        </Card>

        <Card
          title="今週の打ち手"
          extra={<HelpIcon slug="aio/hub" />}
          note="効きは「答えられるようになる問いの数」で書いています。売上や CV への寄与には読み替えていません。"
        >
          {coverage.nextActions.length === 0 ? (
            <p className="vd-stat-note" style={{ margin: 0 }}>
              いま出せる提案はありません。打ち手は、上の 5
              工程のうち詰まっている工程から選びます。
            </p>
          ) : (
            <div className="vd-stack">
              {coverage.nextActions.map((action) => (
                <div key={action.id}>
                  <Link to={action.route} style={{ fontWeight: 600 }}>
                    {action.title}
                  </Link>
                  <p
                    style={{
                      margin: "2px 0 0",
                      color: "var(--vd-text-secondary)",
                    }}
                  >
                    {action.detail}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
